import React, { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Form } from '@shared/components';
import {
    smriqPlanSchema,
    type SmriqPlanFormInput,
    type SmriqPlanFormOutput,
} from '../../../schemas/smriqJourney.schema';
import type { SmriqJourneyError } from '../../../hooks/useSmriqJourney';
import { isSmriqSessionExpired } from '../../../utils/smriqSessionExpired';
import {
    PLAN_CONTROLS_ERROR_FIELDS,
    planControlsFieldMessage,
} from '../../../utils/smriqServiceOrderError';
import { todayDateKey } from '@features/quote/utils/dateKey';
import { DurationField } from './SmriqPlanControls/DurationField';
import { MotField } from './SmriqPlanControls/MotField';
import { PlanStartDateField } from './SmriqPlanControls/PlanStartDateField';

interface SmriqPlanControlsProps {
    className?: string;
    durationMin: number;
    durationMax: number;
    /** Pre-selected duration from product config; null/undefined = none. */
    durationDefault?: number | null;
    /** Configured allowed durations → dropdown; null/undefined = free-type. */
    durationOptions?: number[] | null;
    /** Pre-selected MOT inclusion from product config; null/undefined = the dealer must choose. */
    motDefault?: boolean | null;
    /** False hides the MOT control and sends the default (only false when a default exists). */
    allowMotSelection?: boolean;
    /** The saved plan's start date; today when there is none. */
    planStartDateDefault?: string;
    hasPlan: boolean;
    isStale: boolean;
    isGenerating: boolean;
    generateError: SmriqJourneyError | null;
    needsReconfirm: boolean;
    planSettingsUnknown: boolean;
    /** False blocks Generate/Update; the message shows here only when no panel carries it. */
    canGeneratePlan?: boolean;
    selectionValid?: boolean;
    lastCompletedServiceInPanel?: boolean;
    onGenerate: (payload: {
        duration_months: number;
        include_mot_tests: boolean;
        plan_start_date: string;
    }) => void;
    onFieldsChange: () => void;
    /** Reports the unsent start date, which caps the last completed service date. */
    onPlanStartDateChange?: (date: string) => void;
    /** Discards the edit and closes back to the plan summary. Omitted when there is nothing to
     *  return to (no plan yet, a re-confirm/regenerate is required, or settings unrestored). */
    onCancel?: () => void;
    onRestart: () => void;
}

/** Start date, duration, MOT toggle and the single explicit "Generate plan" action. */
export const SmriqPlanControls: React.FC<SmriqPlanControlsProps> = ({
    className,
    durationMin,
    durationMax,
    durationDefault,
    durationOptions,
    motDefault,
    allowMotSelection = true,
    planStartDateDefault,
    hasPlan,
    isStale,
    isGenerating,
    generateError,
    needsReconfirm,
    planSettingsUnknown,
    canGeneratePlan = true,
    selectionValid = true,
    lastCompletedServiceInPanel = false,
    onGenerate,
    onFieldsChange,
    onPlanStartDateChange,
    onCancel,
    onRestart,
}) => {
    const hasDurationOptions = Array.isArray(durationOptions) && durationOptions.length > 0;
    // Joined, so a new array of the same months does not rebuild the schema every render.
    const durationOptionsKey = hasDurationOptions ? durationOptions.join(',') : '';
    const allowedDurations = useMemo(
        () => (durationOptionsKey === '' ? null : durationOptionsKey.split(',').map(Number)),
        [durationOptionsKey],
    );

    const schema = useMemo(
        () => smriqPlanSchema(durationMin, durationMax, allowedDurations),
        [durationMin, durationMax, allowedDurations],
    );
    const resolver = useMemo(() => zodResolver(schema), [schema]);

    // A single-valued range is a fixed duration: locked to that value and shown as text.
    const durationLocked = !hasDurationOptions && durationMin === durationMax;
    // A configured default pre-selects; otherwise a free-type field falls to the min
    // (today's behaviour), while a dropdown stays empty so the dealer picks a listed value.
    const durationInitial =
        durationDefault != null ? String(durationDefault) : hasDurationOptions ? '' : String(durationMin);

    // '' means the dealer must choose; a configured default pre-selects it.
    const motInitial = motDefault === true ? 'yes' : motDefault === false ? 'no' : '';
    // Hidden control still submits the default, which the resolver guarantees exists.
    const motResolved = allowMotSelection ? motInitial : motInitial || 'yes';
    const motChoiceRequired = allowMotSelection && (planSettingsUnknown || motInitial === '');

    const {
        control,
        handleSubmit,
        setError,
    } = useForm<SmriqPlanFormInput, unknown, SmriqPlanFormOutput>({
        mode: 'onBlur',
        resolver,
        defaultValues: {
            // A fixed duration is locked to its value even on resume; otherwise an
            // unrecoverable saved setting is an empty required field, never a silent default.
            duration_months: durationLocked
                ? String(durationMin)
                : planSettingsUnknown
                  ? ''
                  : durationInitial,
            include_mot_tests: allowMotSelection ? (planSettingsUnknown ? '' : motInitial) : motResolved,
            plan_start_date: planStartDateDefault ?? todayDateKey(),
        },
    });

    useEffect(() => {
        PLAN_CONTROLS_ERROR_FIELDS.forEach((field) => {
            const message = planControlsFieldMessage(generateError, field);
            if (message) setError(field, { type: 'server', message });
        });
    }, [generateError, setError]);

    const isSessionExpired = isSmriqSessionExpired(generateError);
    const isRateLimited = generateError?.code === 'rate_limit_exceeded';
    const isUnavailable = generateError?.code === 'service_plan_unavailable';
    const isNotConfigured = generateError?.code === 'service_plan_not_configured';

    // Editing while a generate is in flight would leave the controls describing a
    // schedule the success handler then marks fresh.
    const fieldsDisabled = isSessionExpired || isGenerating;

    // Only the planSettingsUnknown branch below renders this: nothing behind the saved
    // schedule restored there, so name every setting the dealer is re-entering.
    const unrestoredSettingsMessage =
        'The duration and MOT setting behind the saved schedule are not restored with the quote. Set both before updating the plan — until you do, the saved schedule stays exactly as it is.';

    const onSubmit = (data: SmriqPlanFormOutput) =>
        onGenerate({
            duration_months: data.duration_months,
            include_mot_tests: data.include_mot_tests,
            plan_start_date: data.plan_start_date,
        });

    return (
        <div
            className={`bg-mp-surface rounded-xl space-y-4 ${className ?? ''}`}
            role="group"
            aria-label="Service Plan Parameters"
            aria-busy={isGenerating}
        >
            {isSessionExpired && (
                <div role="alert" className="text-sm text-mp-warning space-y-2">
                    <p>
                        {hasPlan
                            ? 'This plan can no longer be changed here. The existing schedule is unaffected and will still be saved with this quote — start the vehicle step again to make changes.'
                            : 'This session has expired before a plan was generated. Start the vehicle step again, then generate the plan.'}
                    </p>
                    <Button type="button" size="sm" variant="outline" onClick={onRestart}>
                        Restart vehicle step
                    </Button>
                </div>
            )}

            {planSettingsUnknown && (
                <p role="status" className="text-sm text-mp-warning">
                    {unrestoredSettingsMessage}
                </p>
            )}

            {needsReconfirm && (
                <p role="status" className="text-sm text-mp-warning">
                    The vehicle on screen is not confirmed against the current plan. Confirm the
                    vehicle again before generating a plan.
                </p>
            )}

            {isRateLimited && (
                <div role="alert" className="text-sm text-mp-error">
                    Too many attempts.{' '}
                    {generateError?.retryAfterSeconds
                        ? `Try again in ${generateError.retryAfterSeconds} seconds.`
                        : 'Try again shortly.'}
                </div>
            )}

            {isUnavailable && (
                <div role="alert" className="text-sm text-mp-error">
                    The service plan could not be reached. Please try again.
                </div>
            )}

            {isNotConfigured && (
                <div role="alert" className="text-sm text-mp-error">
                    This product cannot be configured right now. Choose another product or contact
                    an administrator.
                </div>
            )}

            {!canGeneratePlan && !lastCompletedServiceInPanel && (
                <p role="alert" className="text-sm text-mp-error">
                    A plan cannot be generated for this vehicle yet.
                </p>
            )}

            <Form
                onSubmit={handleSubmit(onSubmit)}
                noValidate
                // The fields are conditional, so the buttons below are pinned to columns 4/5
                // to stay right-aligned however many render.
                className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_1fr_auto_auto] gap-4 items-start"
            >
                <PlanStartDateField
                    control={control}
                    disabled={fieldsDisabled}
                    onFieldsChange={onFieldsChange}
                    onPlanStartDateChange={onPlanStartDateChange}
                />

                <DurationField
                    control={control}
                    lockedMonths={durationLocked ? durationMin : null}
                    durationOptions={durationOptions}
                    fieldsDisabled={fieldsDisabled}
                    onFieldsChange={onFieldsChange}
                />

                <MotField
                    control={control}
                    allowMotSelection={allowMotSelection}
                    motChoiceRequired={motChoiceRequired}
                    motResolved={motResolved}
                    fieldsDisabled={fieldsDisabled}
                    onFieldsChange={onFieldsChange}
                />

                <div className="flex flex-col self-stretch sm:col-start-4">
                    <label className="mb-1.5 text-sm font-medium text-mp-foreground">&nbsp;</label>
                    <Button
                        size="sm"
                        className="whitespace-nowrap"
                        disabled={
                            fieldsDisabled ||
                            isGenerating ||
                            needsReconfirm ||
                            !canGeneratePlan ||
                            !selectionValid ||
                            (hasPlan && !isStale)
                        }
                    >
                        {isGenerating ? 'Generating...' : hasPlan ? 'Update plan' : 'Generate plan'}
                    </Button>
                </div>
                {onCancel && (
                    <div className="flex flex-col self-stretch sm:col-start-5">
                        <label className="mb-1.5 text-sm font-medium text-mp-foreground">&nbsp;</label>
                        <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="whitespace-nowrap"
                            disabled={isGenerating}
                            onClick={onCancel}
                        >
                            Cancel
                        </Button>
                    </div>
                )}
            </Form>
        </div>
    );
};
